import { createApi } from "@reduxjs/toolkit/query/react";
import { baseApiQuery } from "../../../shared/api";
import type {
  AppealPenaltyBody,
  ConnectDashboardResponse,
  ConnectMatchDetailResponse,
  ConnectMatchesResponse,
  ConnectReportsResponse,
  ConnectScoresResponse,
  ConnectWeightPeriod,
  ConnectWeightsResponse,
  CreateConnectWeightBody,
  GetConnectMatchesParams,
  GetConnectReportsParams,
  GetConnectScoresParams,
  UpdateConnectWeightBody,
} from "./types";

export const connectApi = createApi({
  reducerPath: "connectApi",
  baseQuery: baseApiQuery,
  tagTypes: [
    "ConnectDashboard",
    "ConnectMatches",
    "ConnectMatch",
    "ConnectReports",
    "ConnectScores",
    "ConnectWeights",
  ],
  endpoints: (builder) => ({
    getConnectDashboard: builder.query<ConnectDashboardResponse, void>({
      query: () => "/admin/connect/dashboard",
      providesTags: ["ConnectDashboard"],
    }),

    exportConnectLeaderboardXlsx: builder.query<
      Blob,
      { period?: string } | void
    >({
      query: (params) => {
        const qp = new URLSearchParams();
        if (params?.period) qp.append("period", params.period);
        const qs = qp.toString();
        return {
          url: `/admin/connect/leaderboard/export/xlsx${qs ? `?${qs}` : ""}`,
          method: "GET",
          responseHandler: async (response) => response.blob(),
        };
      },
    }),

    getConnectMatches: builder.query<
      ConnectMatchesResponse,
      GetConnectMatchesParams
    >({
      query: (params) => {
        const qp = new URLSearchParams();
        if (params.month != null) qp.append("month", String(params.month));
        if (params.year != null) qp.append("year", String(params.year));
        if (params.status) qp.append("status", params.status);
        if (params.sort) qp.append("sort", params.sort);
        if (params.order) qp.append("order", params.order);
        if (params.pageToken) qp.append("pageToken", params.pageToken);
        if (params.limit != null) qp.append("limit", String(params.limit));
        const qs = qp.toString();
        return `/admin/connect/matches${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["ConnectMatches"],
    }),

    getConnectMatch: builder.query<ConnectMatchDetailResponse, number>({
      query: (id) => `/admin/connect/matches/${id}`,
      providesTags: (_res, _err, id) => [{ type: "ConnectMatch", id }],
    }),

    getConnectReports: builder.query<
      ConnectReportsResponse,
      GetConnectReportsParams
    >({
      query: (params) => {
        const qp = new URLSearchParams();
        if (params.isHidden !== undefined)
          qp.append("isHidden", String(params.isHidden));
        if (params.search) qp.append("search", params.search);
        if (params.sort) qp.append("sort", params.sort);
        if (params.order) qp.append("order", params.order);
        if (params.pageToken) qp.append("pageToken", params.pageToken);
        if (params.limit != null) qp.append("limit", String(params.limit));
        const qs = qp.toString();
        return `/admin/connect/reports${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["ConnectReports"],
    }),

    hideConnectReport: builder.mutation<void, number>({
      query: (id) => ({
        url: `/admin/connect/reports/${id}/hide`,
        method: "PUT",
      }),
      invalidatesTags: ["ConnectReports", "ConnectMatches", "ConnectMatch"],
    }),

    showConnectReport: builder.mutation<void, number>({
      query: (id) => ({
        url: `/admin/connect/reports/${id}/show`,
        method: "PUT",
      }),
      invalidatesTags: ["ConnectReports", "ConnectMatches", "ConnectMatch"],
    }),

    updateConnectReportInsight: builder.mutation<
      void,
      { id: number; insight: string }
    >({
      query: ({ id, insight }) => ({
        url: `/admin/connect/reports/${id}/insight`,
        method: "PUT",
        body: { insight },
      }),
      invalidatesTags: ["ConnectReports", "ConnectMatches", "ConnectMatch"],
    }),

    getConnectScores: builder.query<
      ConnectScoresResponse,
      GetConnectScoresParams
    >({
      query: (params) => {
        const qp = new URLSearchParams();
        if (params.month != null) qp.append("month", String(params.month));
        if (params.year != null) qp.append("year", String(params.year));
        if (params.reason) qp.append("reason", params.reason);
        if (params.userId) qp.append("userId", params.userId);
        if (params.sort) qp.append("sort", params.sort);
        if (params.order) qp.append("order", params.order);
        if (params.pageToken) qp.append("pageToken", params.pageToken);
        if (params.limit != null) qp.append("limit", String(params.limit));
        const qs = qp.toString();
        return `/admin/connect/scores${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["ConnectScores"],
    }),

    appealConnectPenalty: builder.mutation<
      void,
      { logId: number; body: AppealPenaltyBody }
    >({
      query: ({ logId, body }) => ({
        url: `/admin/connect/scores/${logId}/appeal`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["ConnectScores", "ConnectDashboard", "ConnectMatches"],
    }),

    getConnectWeights: builder.query<ConnectWeightsResponse, void>({
      query: () => "/admin/connect/weights",
      providesTags: ["ConnectWeights"],
    }),

    createConnectWeight: builder.mutation<
      ConnectWeightPeriod,
      CreateConnectWeightBody
    >({
      query: (body) => ({
        url: "/admin/connect/weights",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ConnectWeights"],
    }),

    updateConnectWeight: builder.mutation<
      ConnectWeightPeriod,
      { id: number; body: UpdateConnectWeightBody }
    >({
      query: ({ id, body }) => ({
        url: `/admin/connect/weights/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["ConnectWeights"],
    }),

    deleteConnectWeight: builder.mutation<void, number>({
      query: (id) => ({
        url: `/admin/connect/weights/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ConnectWeights"],
    }),
  }),
});

export const {
  useGetConnectDashboardQuery,
  useLazyExportConnectLeaderboardXlsxQuery,
  useGetConnectMatchesQuery,
  useGetConnectMatchQuery,
  useGetConnectReportsQuery,
  useHideConnectReportMutation,
  useShowConnectReportMutation,
  useUpdateConnectReportInsightMutation,
  useGetConnectScoresQuery,
  useAppealConnectPenaltyMutation,
  useGetConnectWeightsQuery,
  useCreateConnectWeightMutation,
  useUpdateConnectWeightMutation,
  useDeleteConnectWeightMutation,
} = connectApi;
